/**
 * MOCK DATA + factories — VER-108 → VER-109 native survey builder.
 *
 * Factories build blank/duplicated entities in the section-based model. The
 * builder is FRONTEND-ONLY in this pass: user-built surveys live in component
 * state (or are round-tripped through the injected persistence callbacks in
 * builder.persistence.ts, which default to an in-memory store).
 */
import type {
    BranchingRule,
    ScaleOption,
    Section,
    SurveyDefinition,
    SurveyQuestion,
    QuestionType,
} from "./survey.types";

/** A fresh, editable default Likert scale (3 sensible default points). */
export const newDefaultScale = (): ScaleOption[] => [
    { label: "Disagree", value: 1 },
    { label: "Neutral", value: 2 },
    { label: "Agree", value: 3 },
];

/** Generate a process-unique id with a readable prefix. */
let idCounter = 0;
export const nextLocalId = (prefix: string): string => {
    idCounter += 1;
    return `${prefix}-${Date.now().toString(36)}-${idCounter}`;
};

/** Type-appropriate defaults for a brand-new question of a given type. */
const defaultsForType = (type: QuestionType): Partial<SurveyQuestion> => {
    switch (type) {
        case "multi":
            return {
                scored: false,
                options: [
                    { label: "Option 1", value: 1 },
                    { label: "Option 2", value: 2 },
                    { label: "Option 3", value: 3 },
                ],
            };
        case "freeText":
            return { scored: false, freeText: { multiline: true } };
        case "vas":
            return { scored: true, vas: { min: 0, max: 100, minLabel: "Not at all", maxLabel: "Extremely", step: 1 } };
        case "nps":
            return { scored: true };
        case "single":
        case "likert":
        default:
            return { scored: true };
    }
};

/** A blank question of the given type (default likert), ready to edit. */
export const newQuestion = (order: number, type: QuestionType = "likert"): SurveyQuestion => ({
    id: nextLocalId("q"),
    order,
    text: "",
    type,
    reverseScored: false,
    scored: true,
    required: true,
    ...defaultsForType(type),
});

/**
 * Change a question's type, applying that type's sensible defaults while keeping
 * its id / order / text / required. Config for the OLD type (options/vas/freeText)
 * is dropped when it no longer applies, so the definition never carries stale
 * config that could confuse scoring or the runner.
 */
export const applyTypeChange = (question: SurveyQuestion, type: QuestionType): SurveyQuestion => ({
    id: question.id,
    order: question.order,
    text: question.text,
    required: question.required,
    type,
    reverseScored: false,
    scored: true,
    // strip old-type config, then layer in the new type's defaults
    options: undefined,
    vas: undefined,
    freeText: undefined,
    ...defaultsForType(type),
});

/** A blank section with one empty question. */
export const newSection = (order: number): Section => ({
    id: nextLocalId("section"),
    order,
    title: "",
    questions: [newQuestion(1)],
});

/**
 * A blank, editable survey skeleton: one section, one question, a 3-point scale.
 * Drafts start at version 0 — the first publish bumps them to v1.
 */
export const newSurvey = (): SurveyDefinition => ({
    id: nextLocalId("survey"),
    code: "",
    title: "",
    instructions: "",
    higherIsBetter: true,
    status: "draft",
    version: 0,
    defaultScale: { options: newDefaultScale() },
    sections: [newSection(1)],
    scoring: {
        method: "sum",
        rawRange: [0, 0],
        // multiplier + cutoff are optional and left unset by default.
    },
    branchingRules: [],
    isSystemSeed: false,
});

/** Deep-copy a question with a fresh id. */
export const duplicateQuestion = (source: SurveyQuestion, order: number): SurveyQuestion => ({
    ...source,
    id: nextLocalId("q"),
    order,
    options: source.options ? source.options.map((o) => ({ ...o })) : undefined,
    vas: source.vas ? { ...source.vas } : undefined,
    freeText: source.freeText ? { ...source.freeText } : undefined,
});

/**
 * Deep-copy a section with a fresh id + fresh question ids. Branching rules
 * referencing the original are intentionally NOT remapped onto the copy.
 */
export const duplicateSection = (source: Section, order: number): Section => ({
    id: nextLocalId("section"),
    order,
    title: source.title ? `${source.title} (copy)` : "",
    description: source.description,
    questions: source.questions.map((q, i) => duplicateQuestion(q, i + 1)),
});

/**
 * Clone a survey into a fresh editable draft: re-ids the survey, every section
 * and question, remaps branching rules onto the new ids, strips isSystemSeed,
 * resets to draft v0 and appends "(copy)".
 */
export const cloneSurvey = (source: SurveyDefinition): SurveyDefinition => {
    const idMap = new Map<string, string>();

    const sections: Section[] = source.sections.map((section) => {
        const newSectionId = nextLocalId("section");
        idMap.set(section.id, newSectionId);
        return {
            ...section,
            id: newSectionId,
            questions: section.questions.map((q) => {
                const newQuestionId = nextLocalId("q");
                idMap.set(q.id, newQuestionId);
                return {
                    ...q,
                    id: newQuestionId,
                    options: q.options ? q.options.map((o) => ({ ...o })) : undefined,
                    vas: q.vas ? { ...q.vas } : undefined,
                    freeText: q.freeText ? { ...q.freeText } : undefined,
                };
            }),
        };
    });

    const branchingRules: BranchingRule[] = source.branchingRules.map((rule) => ({
        ...rule,
        id: nextLocalId("rule"),
        sourceQuestionId: idMap.get(rule.sourceQuestionId) ?? rule.sourceQuestionId,
        targetId: idMap.get(rule.targetId) ?? rule.targetId,
    }));

    return {
        ...source,
        id: nextLocalId("survey"),
        code: source.code ? `${source.code}_COPY` : "",
        title: `${source.title} (copy)`,
        status: "draft",
        version: 0,
        defaultScale: { options: source.defaultScale.options.map((o) => ({ ...o })) },
        sections,
        scoring: { ...source.scoring, cutoff: source.scoring.cutoff ? { ...source.scoring.cutoff } : undefined },
        branchingRules,
        // Deliberately drop the source licence: a clone is editable, so once the question
        // text changes the official WHO-5/UCLA citation would no longer apply. Overrides
        // the `...source` spread above (which would otherwise carry `licence` across).
        licence: undefined,
        isSystemSeed: false,
    };
};

/**
 * Seed editable (user-built) surveys shown under "Your surveys", alongside the
 * read-only reference scales. Deliberately a mix of a DRAFT (continue editing via
 * the Edit button) and a PUBLISHED survey (editing warns → clone) so the builder's
 * edit/clone lifecycle is demonstrable. Frontend-only mock; the persistence store
 * seeds from this list.
 */
const STAFF_PULSE_DRAFT: SurveyDefinition = {
    id: "user-staff-pulse",
    code: "STAFFPULSE",
    title: "Staff Wellbeing Pulse",
    instructions: "A short monthly pulse for the staff team. Still being drafted.",
    higherIsBetter: true,
    status: "draft",
    version: 0,
    defaultScale: {
        options: [
            { label: "Strongly disagree", value: 1 },
            { label: "Disagree", value: 2 },
            { label: "Neutral", value: 3 },
            { label: "Agree", value: 4 },
            { label: "Strongly agree", value: 5 },
        ],
    },
    sections: [
        {
            id: "section-1",
            order: 1,
            title: "This month",
            questions: [
                {
                    id: "q1",
                    order: 1,
                    text: "I feel supported at work.",
                    type: "likert",
                    reverseScored: false,
                    scored: true,
                    required: true,
                },
                {
                    id: "q2",
                    order: 2,
                    text: "I have a manageable workload.",
                    type: "likert",
                    reverseScored: false,
                    scored: true,
                    required: true,
                },
                {
                    id: "q3",
                    order: 3,
                    text: "What would make next month better?",
                    type: "freeText",
                    reverseScored: false,
                    scored: false,
                    required: false,
                    freeText: { multiline: true, maxLength: 500 },
                },
            ],
        },
    ],
    scoring: { method: "sum", rawRange: [2, 10] },
    branchingRules: [],
    isSystemSeed: false,
};

const PARENT_FEEDBACK_PUBLISHED: SurveyDefinition = {
    id: "user-parent-feedback",
    code: "PARFB",
    title: "Parent Feedback",
    instructions: "Feedback from parents at the end of the programme.",
    higherIsBetter: true,
    status: "published",
    version: 2,
    defaultScale: { options: [] },
    sections: [
        {
            id: "section-1",
            order: 1,
            title: "Your feedback",
            questions: [
                {
                    id: "q1",
                    order: 1,
                    text: "How well did the programme support your child?",
                    type: "single",
                    reverseScored: false,
                    scored: true,
                    required: true,
                    options: [
                        { label: "Not at all", value: 1 },
                        { label: "A little", value: 2 },
                        { label: "Somewhat", value: 3 },
                        { label: "Well", value: 4 },
                        { label: "Very well", value: 5 },
                    ],
                },
                {
                    id: "q2",
                    order: 2,
                    text: "How likely are you to recommend it to another parent?",
                    type: "nps",
                    reverseScored: false,
                    scored: true,
                    required: true,
                },
                {
                    id: "q3",
                    order: 3,
                    text: "Any comments?",
                    type: "freeText",
                    reverseScored: false,
                    scored: false,
                    required: false,
                    freeText: { multiline: true, maxLength: 500 },
                },
            ],
        },
    ],
    scoring: { method: "sum", rawRange: [1, 15] },
    branchingRules: [],
    isSystemSeed: false,
};

export const MOCK_USER_SURVEYS: SurveyDefinition[] = [STAFF_PULSE_DRAFT, PARENT_FEEDBACK_PUBLISHED];
